import { Component, Input, QueryList, ChangeDetectionStrategy, ChangeDetectorRef, ElementRef, AfterViewInit, ContentChildren, Output, EventEmitter } from '@angular/core';
import { SplitterPanelComponent } from './splitter-panel.component';

export interface NcSplitterResizeEvent {
  gutterIndex: number;
  sizes: number[];
  delta?: number;
  mouseEvent: MouseEvent;
  containerSize: [number, number];
}

@Component({
  selector: 'nc-splitter',
  templateUrl: './splitter.component.html',
  styleUrls: ['./splitter.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SplitterComponent implements AfterViewInit {
  @Input() ncLayout: 'horizontal' | 'vertical' = 'horizontal';
  @Input() ncGutterSize: number = 4;
  @Input() ncGutterColor: string = '#e2e7ef';
  @ContentChildren(SplitterPanelComponent) panelComponents!: QueryList<SplitterPanelComponent>;
  @Output() ncResize = new EventEmitter<NcSplitterResizeEvent>();
  @Output() ncResizeStart = new EventEmitter<NcSplitterResizeEvent>();
  @Output() ncResizeEnd = new EventEmitter<NcSplitterResizeEvent>();

  panels: SplitterPanelComponent[] = [];
  isResizing = false;
  resizingIndex = -1;
  startPosition = 0;
  startSizes: number[] = [];

  constructor(private cdr: ChangeDetectorRef, private el: ElementRef) { }

  ngAfterViewInit() {
    this.panels = this.panelComponents.toArray();
    this.normalizeSizes();
    this.cdr.detectChanges();
  }

  normalizeSizes() {
    // 计算已设置尺寸的面板总和
    const configuredPanels = this.panels.filter(panel => panel.ncPanelSize);
    const configuredSize = configuredPanels.reduce((sum, panel) => sum + panel.ncPanelSize!, 0);
    
    // 计算未设置尺寸的面板数量
    const unconfiguredCount = this.panels.length - configuredPanels.length;
    
    // 为未设置尺寸的面板平均分配剩余空间
    const remainingSize = 100 - configuredSize;
    const defaultSize = unconfiguredCount > 0 ? remainingSize / unconfiguredCount : 0;
    
    // 设置每个面板的最终尺寸
    this.panels.forEach(panel => {
      if (!panel.ncPanelSize) {
        panel.ncPanelSize = defaultSize;
      }
    });
  }

  onMouseDown(event: MouseEvent, index: number) {
    this.isResizing = true;
    this.resizingIndex = index;
    this.startPosition = this.ncLayout === 'horizontal' ? event.clientX : event.clientY;
    this.startSizes = this.panels.map(panel => panel.ncPanelSize || 0);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
    this.ncResizeStart.emit({
      gutterIndex: index,
      sizes: this.panels.map(panel => panel.ncPanelSize || 0),
      mouseEvent: event,
      containerSize: [this.el.nativeElement.offsetWidth, this.el.nativeElement.offsetHeight]
    });
  }

  onMouseMove = (event: MouseEvent) => {
    if (!this.isResizing) return;
    const currentPosition = this.ncLayout === 'horizontal' ? event.clientX : event.clientY;
    const delta = ((currentPosition - this.startPosition) / this.getContainerSize()) * 100;

    const newSizes = [...this.startSizes];
    newSizes[this.resizingIndex] = Math.max(this.panels[this.resizingIndex].ncMinSize || 0, this.startSizes[this.resizingIndex] + delta);
    newSizes[this.resizingIndex + 1] = Math.max(this.panels[this.resizingIndex + 1].ncMinSize || 0, this.startSizes[this.resizingIndex + 1] - delta);

    const totalSize = newSizes.reduce((sum, size) => sum + size, 0);
    this.panels.forEach((panel, i) => panel.ncPanelSize = (newSizes[i] / totalSize) * 100);

    this.ncResize.emit({
      gutterIndex: this.resizingIndex,
      sizes: this.panels.map(panel => panel.ncPanelSize || 0),
      delta: delta,
      mouseEvent: event,
      containerSize: [this.el.nativeElement.offsetWidth, this.el.nativeElement.offsetHeight]
    });
    this.cdr.detectChanges();
  }

  onMouseUp = (event: MouseEvent) => {
    this.isResizing = false;
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    this.ncResizeEnd.emit({
      gutterIndex: this.resizingIndex,
      sizes: this.panels.map(panel => panel.ncPanelSize || 0),
      mouseEvent: event,
      containerSize: [this.el.nativeElement.offsetWidth, this.el.nativeElement.offsetHeight]
    });
  }

  getContainerSize(): number {
    return this.ncLayout === 'horizontal' ? this.el.nativeElement.offsetWidth : this.el.nativeElement.offsetHeight;
  }
}